'use client';

import * as React from 'react';

import { Plate, usePlateEditor } from 'platejs/react';
import { Editor, EditorContainer } from '@/components/ui/platejs/editor';
import { EditorKit } from './editor-kit';
import { useDebounce } from '@/hooks/use-debounce';
import { MarkdownPlugin } from '@platejs/markdown';
import { Task, useTaskStore } from '@/stores/TaskStore';
import { MaterialUISwitch } from '@/components/ui/CustomSwitch';

// 1. 创建一个包含所有 Hooks 和渲染逻辑的内部组件
function EditorCore({ task }: { task: Task }) {
  const { id, comment } = task;
  const taskStore = useTaskStore();

  const [showMarkdown, setShowMarkdown] = React.useState(false);

  // 现在所有 Hooks 都在组件的顶层被无条件调用
  const [markdownValue, setMarkdownValue] = React.useState(comment || '');
  const debouncedMarkdownValue = useDebounce(markdownValue, 300);

  React.useEffect(() => {
    if (comment !== markdownValue) {
      setMarkdownValue(comment || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, comment]);

  const markdownEditor = usePlateEditor({
    plugins: [],
    id: `markdown-editor-${id}`,
    value: [{ children: [{ text: markdownValue }], type: 'p' }],
  });

  const editor = usePlateEditor(
    {
      plugins: EditorKit,
      id: `rich-text-editor-${id}`,
      value: (editor) =>
        editor.getApi(MarkdownPlugin).markdown.deserialize(markdownValue),
    },
    []
  );

  React.useEffect(() => {
    // 在这里更新 store
    taskStore.updateTask(id, { comment: debouncedMarkdownValue });
    console.log('已保存的值:', debouncedMarkdownValue);
    editor.tf.reset();
    editor.tf.setValue(
      editor.api.markdown.deserialize(debouncedMarkdownValue)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMarkdownValue, editor]);

  return (
    <div className="relative h-full">
      <div className="absolute right-2 top-2 z-10">
        <MaterialUISwitch
          checked={showMarkdown}
          onChange={() => setShowMarkdown(!showMarkdown)}
        />
      </div>
      {showMarkdown ? (
        <Plate
          onValueChange={() => {
            const value = markdownEditor.children
              .map((node) => markdownEditor.api.string(node))
              .join('\n');
            setMarkdownValue(value);
            console.log('编辑器当前值:', value);
          }}
          editor={markdownEditor}
        >
          <EditorContainer className="h-full">
            <Editor
              variant="none"
              className="bg-muted/50 p-4 font-mono text-sm text-foreground h-full overflow-y-auto"
            />
          </EditorContainer>
        </Plate>
      ) : (
        <Plate editor={editor} readOnly={true}>
          <EditorContainer className="h-full">
            <Editor
              variant="none"
              className="bg-muted/50 p-4 text-foreground h-full overflow-y-auto"
            />
          </EditorContainer>
        </Plate>
      )}
    </div>
  );
}

export default function MyEditor({ id }: { id: string }) {
  const taskStore = useTaskStore();
  const task = taskStore.getTaskById(id);

  // 2. 在顶层组件中处理条件返回
  if (!task) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <p className="text-muted-foreground">任务未找到或正在加载...</p>
      </div>
    );
  }

  // 3. 当 task 存在时，渲染核心编辑器组件
  return <EditorCore task={task} />;
}